import React, { useState, useEffect } from 'react';
import { fetchMediaDetail } from '../services/api';
import { Skeleton } from './common';
import './MediaDetailView.css';

/**
 * Extract YouTube video ID from watch URL or short URL.
 * @param {string} mediaUrl - e.g. https://youtube.com/watch?v=ID or https://youtu.be/ID
 * @returns {string|null} Video ID or null
 */
function getYouTubeVideoId(mediaUrl) {
  if (!mediaUrl || typeof mediaUrl !== 'string') return null;
  const url = mediaUrl.trim();
  try {
    if (url.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
      return match ? match[1] : null;
    }
    if (url.includes('youtube.com/watch') && url.includes('v=')) {
      const u = new URL(url);
      return u.searchParams.get('v') || null;
    }
    if (url.includes('youtube.com/embed/')) {
      const match = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
      return match ? match[1] : null;
    }
  } catch (_) {
    return null;
  }
  return null;
}

/**
 * Extract TikTok video ID from URL.
 * Supports formats:
 * - https://www.tiktok.com/@username/video/7123456789
 * - https://tiktok.com/@username/video/7123456789
 * - https://vm.tiktok.com/XXXXXXXXX (short URL - returns null, can't embed directly)
 * @param {string} mediaUrl - TikTok video URL
 * @returns {string|null} Video ID or null
 */
function getTikTokVideoId(mediaUrl) {
  if (!mediaUrl || typeof mediaUrl !== 'string') return null;
  const url = mediaUrl.trim();
  try {
    // Pattern: /@username/video/VIDEO_ID or /video/VIDEO_ID
    const match = url.match(/\/video\/(\d+)/);
    return match ? match[1] : null;
  } catch (_) {
    return null;
  }
}

/**
 * Media Detail View Component
 * Full media details in a single Overview: tags, hook, USP, CTA, what it's about,
 * regional rankings, and learn-from content (why it works, hook analysis, performance, style, structure, borrow, differentiate).
 * No separate Structure or Engagement Stats sections.
 *
 * @param {Object} props
 * @param {string} props.trendId - Trend ID to display
 * @param {Function} props.onBack - Callback to go back
 * @param {Function} props.onTagClick - Callback when tag is clicked
 */
function MediaDetailView({ trendId, onBack, onTagClick }) {
  const [mediaData, setMediaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMediaDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMediaDetail(trendId);
        setMediaData(data);
      } catch (err) {
        setError('Failed to load media details. Please try again.');
        console.error('Failed to load media detail:', err);
      } finally {
        setLoading(false);
      }
    };
    loadMediaDetail();
  }, [trendId]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="media-detail-view">
        <header className="detail-header">
          <button className="back-btn" onClick={onBack}>← Back to Videos</button>
        </header>
        <div className="loading-skeleton">
          {/* Video skeleton */}
          <div className="skeleton-video">
            <Skeleton height="100%" borderRadius={12} />
          </div>
          {/* Info skeleton */}
          <div className="skeleton-info">
            <Skeleton height="28px" width="80%" />
            <Skeleton height="18px" width="40%" />
            <div className="skeleton-metrics">
              <Skeleton height="20px" width="80px" />
              <Skeleton height="20px" width="80px" />
              <Skeleton height="20px" width="80px" />
            </div>
          </div>
          {/* Content skeleton */}
          <div className="skeleton-sections">
            <Skeleton height="24px" width="200px" />
            <Skeleton height="60px" width="100%" borderRadius={8} />
            <Skeleton height="24px" width="180px" />
            <Skeleton height="80px" width="100%" borderRadius={8} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !mediaData) {
    return (
      <div className="media-detail-view">
        <div className="error-state">
          <p>{error || 'Media not found'}</p>
          <button onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  const openOriginal = () => {
    if (mediaData.media_url) {
      window.open(mediaData.media_url, '_blank');
    }
  };

  const youtubeVideoId = getYouTubeVideoId(mediaData.media_url);
  const tiktokVideoId = getTikTokVideoId(mediaData.media_url);

  // Get rank and score
  const rank = mediaData.rank;
  const score = mediaData.score;

  return (
    <div className="media-detail-view">
      {/* Header */}
      <header className="detail-header">
        <button className="back-btn" onClick={onBack}>← Back to Videos</button>
        {rank && (
          <div className="detail-rank-badge">
            <span className={`rank-number ${rank <= 3 ? 'top-3' : ''}`}>#{rank}</span>
            {score && <span className="rank-score">Score: {typeof score === 'number' ? score.toFixed(1) : score}</span>}
          </div>
        )}
      </header>

      {/* Video player (YouTube/TikTok embed) or thumbnail */}
      {youtubeVideoId ? (
        <div className="media-video-embed">
          <iframe
            title={mediaData.title || 'Video'}
            src={`https://www.youtube.com/embed/${youtubeVideoId}?rel=0`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <span className="media-type-badge media-type-badge--embed">{mediaData.media_type}</span>
        </div>
      ) : tiktokVideoId ? (
        <div className="media-video-embed media-video-embed--tiktok">
          <iframe
            title={mediaData.title || 'TikTok Video'}
            src={`https://www.tiktok.com/embed/v2/${tiktokVideoId}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            scrolling="no"
          />
          <span className="media-type-badge media-type-badge--embed">{mediaData.media_type}</span>
        </div>
      ) : (
        <div className="media-hero media-hero--thumbnail-only">
          <div className="hero-thumbnail">
            <img src={mediaData.thumbnail_url || ''} alt={mediaData.title || ''} />
            <span className="media-type-badge">{mediaData.media_type}</span>
          </div>
        </div>
      )}

      {/* Media Hero: title, badges, metrics */}
      <div className="media-hero media-hero--info">
        <div className="hero-content">
          <h1 className="media-title">{mediaData.title || 'Untitled'}</h1>
          <div className="media-metrics">
            {mediaData.metrics?.views != null && (
              <span className="metric">👁️ {formatNumber(mediaData.metrics.views)} views</span>
            )}
            {mediaData.metrics?.likes != null && (
              <span className="metric">👍 {formatNumber(mediaData.metrics.likes)} likes</span>
            )}
            {mediaData.metrics?.comments != null && (
              <span className="metric">💬 {formatNumber(mediaData.metrics.comments)} comments</span>
            )}
            {mediaData.metrics?.shares != null && (
              <span className="metric">🔗 {formatNumber(mediaData.metrics.shares)} shares</span>
            )}
            {mediaData.metrics?.upvotes != null && (
              <span className="metric">⬆️ {formatNumber(mediaData.metrics.upvotes)} upvotes</span>
            )}
          </div>
          {(mediaData.region_ranks && mediaData.region_ranks.length > 0) && (
            <div className="media-where-ranks">
              <span className="where-ranks-label">Where this ranks</span>
              <span className="where-ranks-list">
                {mediaData.region_ranks.map((r, idx) => (
                  <React.Fragment key={r.region_code || r.region || idx}>
                    {idx > 0 && ' · '}
                    <span className="where-ranks-item">
                      #{r.rank} in {r.region_name || r.region_code || r.region || '—'}
                    </span>
                  </React.Fragment>
                ))}
              </span>
            </div>
          )}
          {(mediaData.published_at || mediaData.collected_at) && (
            <p className="published-date">
              {mediaData.published_at
                ? `Published: ${formatDate(mediaData.published_at)}`
                : `Collected: ${formatDate(mediaData.collected_at)}`}
            </p>
          )}
          <button className="open-original-btn" onClick={openOriginal}>
            Open Original ↗
          </button>
        </div>
      </div>

      {/* Single combined Overview (tags, hook, USP, CTA, what it's about, rankings, learn-from) */}
      <div className="tab-content">
        <OverviewTab data={mediaData} formatNumber={formatNumber} formatDate={formatDate} onTagClick={onTagClick} />
      </div>
    </div>
  );
}

// ============================================
// Tab Components
// ============================================

/**
 * Single combined Overview: Tags, Hook, USP, CTA, What it's about, Regional Rankings,
 * then Learn From This (Why This Works, Hook Analysis, Performance, Style, Structure Breakdown, What to Borrow, How to Differentiate).
 * No Structure (segments) and no Engagement Stats.
 */
function OverviewTab({ data, formatNumber, formatDate, onTagClick }) {
  const regionRanks = data.region_ranks || [];
  const breakdown = data.content_breakdown || {};
  const hasBreakdown = breakdown.hook || breakdown.pacing || breakdown.call_to_action || breakdown.duration_category;
  const analytics = data.analytics && typeof data.analytics === 'object' ? data.analytics : {};
  const styleSignals = data.style_signals && typeof data.style_signals === 'object' ? data.style_signals : {};
  const learnFrom = (data.learn_from_this && typeof data.learn_from_this === 'object') ? data.learn_from_this : {};
  const sourceTagsList = getDisplaySourceTags(data.source_tags);
  const trendingTags = data.referenced_trending_tags || [];

  return (
    <div className="overview-tab">
      {/* Tags (merged from Tags tab) */}
      {(sourceTagsList.length > 0 || trendingTags.length > 0) && (
        <section className="detail-section">
          <h2>📌 Tags</h2>
          {sourceTagsList.length > 0 && (
            <>
              <p className="section-description">Source tags (hashtags, categories)</p>
              <div className="tags-list">
                {sourceTagsList.map((item, idx) => (
                  <div key={idx} className="tag-item" onClick={() => onTagClick && onTagClick(item.tag)}>
                    <span className="tag-name">#{item.tag}</span>
                    {item.source && <span className="tag-type">{item.source}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
          {trendingTags.length > 0 && (
            <>
              <p className="section-description">Related trending tags</p>
              <div className="trending-tags">
                {trendingTags.map((tag, idx) => (
                  <button
                    key={idx}
                    className="trending-tag-btn"
                    onClick={() => onTagClick && onTagClick(typeof tag === 'string' ? tag : tag?.tag_id || tag)}
                  >
                    #{typeof tag === 'string' ? tag : tag?.tag_name || tag?.tag_id || tag}
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Hook (no Structure/segments) */}
      {hasBreakdown && (
        <section className="detail-section content-breakdown-section">
          <h2>🎬 Hook &amp; call to action</h2>
          {breakdown.hook && (
            <div className="content-breakdown-hook">
              <span className="content-breakdown-label">Hook</span>
              <blockquote className="content-breakdown-quote">{breakdown.hook}</blockquote>
            </div>
          )}
          <div className="content-breakdown-meta">
            {breakdown.pacing && (
              <span className="content-breakdown-badge" title="Pacing">
                ⏱️ {breakdown.pacing}
              </span>
            )}
            {breakdown.duration_category && (
              <span className="content-breakdown-badge" title="Duration">
                📏 {breakdown.duration_category}
              </span>
            )}
          </div>
          {breakdown.call_to_action && (
            <div className="content-breakdown-cta">
              <span className="content-breakdown-label">Call to action</span>
              <p className="content-breakdown-cta-text">{breakdown.call_to_action}</p>
            </div>
          )}
        </section>
      )}

      {/* USP (from Analytics) */}
      {analytics.usp && (
        <section className="detail-section">
          <h2>💡 Unique selling point</h2>
          <div className="analytics-card-body">{renderAnalyticsValue(analytics.usp)}</div>
        </section>
      )}

      {/* What it's about */}
      {(data.ai_summary_short || data.description || data.summary) && (
        <section className="detail-section">
          <h2>{data.ai_summary_short ? "What it's about" : 'Description'}</h2>
          <p className="description-text">
            {data.ai_summary_short || data.description || data.summary}
          </p>
        </section>
      )}

      {/* Regional Rankings — where this media ranks in each region */}
      {regionRanks.length > 0 && (
        <section className="detail-section region-ranks-section">
          <h2>Where this ranks by region</h2>
          <p className="section-description">
            This media appears in the trending rankings for the following regions.
          </p>
          <div className="region-ranks">
            {regionRanks.map((region, idx) => (
              <div key={region.region_code || region.region || idx} className="region-rank-item">
                <span className="region-name">{region.region_name || region.region_code || region.region || '—'}</span>
                <span className="region-rank">Rank #{region.rank}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Learn From This: Why This Works */}
      {learnFrom.why_this_works && (
        <section className="detail-section">
          <h2>✨ Why This Works</h2>
          <p className="highlight-text">{learnFrom.why_this_works}</p>
        </section>
      )}

      {/* Hook Analysis */}
      {learnFrom.hook_analysis && (
        <section className="detail-section">
          <h2>🎣 Hook Analysis</h2>
          <div className="hook-analysis">
            <div className="hook-header">
              {learnFrom.hook_analysis.hook_type && (
                <span className="hook-type">{learnFrom.hook_analysis.hook_type.replace(/-/g, ' ')}</span>
              )}
              {learnFrom.hook_analysis.strength != null && (
                <span className="hook-strength">Strength: {learnFrom.hook_analysis.strength}/100</span>
              )}
            </div>
            {learnFrom.hook_analysis.first_seconds_text && (
              <blockquote className="hook-quote">
                &quot;{learnFrom.hook_analysis.first_seconds_text}&quot;
              </blockquote>
            )}
            {learnFrom.hook_analysis.why_effective && (
              <p className="hook-explanation">{learnFrom.hook_analysis.why_effective}</p>
            )}
          </div>
        </section>
      )}

      {/* Performance Insights (from Analytics) */}
      {analytics.performance && (
        <section className="detail-section">
          <h2>📊 Performance Insights</h2>
          <div className="analytics-card-body">{renderAnalyticsValue(analytics.performance)}</div>
        </section>
      )}

      {/* Style Signals */}
      {(styleSignals.format || styleSignals.visual_style || styleSignals.pacing || styleSignals.editing) && (
        <section className="detail-section">
          <h2>🎨 Style Signals</h2>
          <div className="style-grid">
            {styleSignals.format && (
              <div className="style-item">
                <span className="style-label">Format</span>
                <span className="style-value">{String(styleSignals.format)}</span>
              </div>
            )}
            {styleSignals.visual_style && (
              <div className="style-item">
                <span className="style-label">Visual Style</span>
                <span className="style-value">{String(styleSignals.visual_style).replace(/_/g, ' ')}</span>
              </div>
            )}
            {styleSignals.pacing && (
              <div className="style-item">
                <span className="style-label">Pacing</span>
                <span className="style-value">{String(styleSignals.pacing)}</span>
              </div>
            )}
            {styleSignals.editing && (
              <div className="style-item">
                <span className="style-label">Editing</span>
                <span className="style-value">{String(styleSignals.editing)}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Structure Breakdown (from Learn From This) */}
      {learnFrom.structure_breakdown && learnFrom.structure_breakdown.sections && (
        <section className="detail-section">
          <h2>📐 Structure Breakdown</h2>
          <div className="structure-timeline">
            {learnFrom.structure_breakdown.sections.map((section, idx) => (
              <div key={idx} className="timeline-item">
                <span className="timeline-time">{section.time_range}</span>
                <div className="timeline-content">
                  <span className="timeline-section">{section.section}</span>
                  <p className="timeline-desc">{section.content}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="structure-meta">
            {learnFrom.structure_breakdown.pacing && (
              <span className="meta-item">Pacing: {learnFrom.structure_breakdown.pacing}</span>
            )}
            {learnFrom.structure_breakdown.transitions && (
              <span className="meta-item">Transitions: {learnFrom.structure_breakdown.transitions}</span>
            )}
          </div>
        </section>
      )}

      {/* What to Borrow */}
      {learnFrom.what_to_borrow && learnFrom.what_to_borrow.length > 0 && (
        <section className="detail-section">
          <h2>📋 What to Borrow</h2>
          <ul className="borrow-list">
            {learnFrom.what_to_borrow.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      {/* How to Differentiate */}
      {learnFrom.how_to_differentiate && learnFrom.how_to_differentiate.length > 0 && (
        <section className="detail-section">
          <h2>🎯 How to Differentiate</h2>
          <ul className="differentiate-list">
            {learnFrom.how_to_differentiate.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Empty state when nothing to show */}
      {sourceTagsList.length === 0 && trendingTags.length === 0 && !hasBreakdown && !analytics.usp &&
        !data.ai_summary_short && !data.description && !data.summary && regionRanks.length === 0 &&
        !learnFrom.why_this_works && !learnFrom.hook_analysis && !analytics.performance &&
        !styleSignals.format && !styleSignals.visual_style && !styleSignals.pacing && !styleSignals.editing &&
        !(learnFrom.structure_breakdown && learnFrom.structure_breakdown.sections?.length) &&
        !(learnFrom.what_to_borrow?.length) && !(learnFrom.how_to_differentiate?.length) && (
        <div className="empty-section">
          <p>No overview data available for this media.</p>
        </div>
      )}
    </div>
  );
}

/** Build display list from source_tags (object with hashtags/categories or array). Keywords are excluded. */
function getDisplaySourceTags(sourceTags) {
  if (!sourceTags) return [];
  if (Array.isArray(sourceTags)) {
    return sourceTags.map((t) => ({ tag: typeof t === 'string' ? t : t?.tag, source: typeof t === 'object' && t != null ? t.source : '' })).filter((t) => t.tag);
  }
  const out = [];
  (sourceTags.hashtags || []).forEach((t) => out.push({ tag: typeof t === 'string' ? t : t?.tag || t, source: 'hashtag' }));
  (sourceTags.categories || []).forEach((t) => out.push({ tag: typeof t === 'string' ? t : t?.tag || t, source: 'category' }));
  return out.filter((t) => t && t.tag);
}

/** Render a value in a human-friendly way (no raw JSON). Used by Overview for USP/performance. */
function renderAnalyticsValue(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return (
      <ul className="analytics-value-list">
        {value.map((item, idx) => (
          <li key={idx}>
            {typeof item === 'object' && item !== null ? renderAnalyticsValue(item) : String(item)}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(([, v]) => v != null && v !== '');
    if (entries.length === 0) return null;
    return (
      <dl className="analytics-value-dl">
        {entries.map(([k, v]) => (
          <div key={k} className="analytics-value-row">
            <dt className="analytics-value-dt">{k.replace(/_/g, ' ')}</dt>
            <dd className="analytics-value-dd">
              {typeof v === 'string' ? v : Array.isArray(v) || (typeof v === 'object' && v !== null) ? renderAnalyticsValue(v) : String(v)}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return String(value);
}

export default MediaDetailView;
